"use client"
import { type CSSProperties, useRef } from "react"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"
import type { CtaLink } from "~/types/cms"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { SideBySideInterface } from "./SideBySide.interface"
import s from "./SideBySide.module.scss"

export type SideBySideViewProps = Omit<SideBySideInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
  cta: CtaLink | null
}

export const SideBySideView = ({
  asset,
  headline,
  body,
  imageRight,
  config,
  moduleIndex,
  className,
  style,
  cta,
}: SideBySideViewProps) => {
  const configProps = getModuleConfigProps(config)
  const imageRef = useRef<HTMLDivElement>(null)

  return (
    <section
      className={cn(s.sideBySide, `stack-module-${moduleIndex}`, className)}
      data-module="side-by-side"
      data-image-right={imageRight}
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn(s.inner, "container")}>
        <div ref={imageRef} className={s.imageCol}>
          {asset?.desktop && (
            <ResponsiveAsset
              className={s.image}
              desktop={asset.desktop}
              mobile={asset.mobile ?? undefined}
              imageSizes={{ base: "100vw", md: "50vw" }}
            />
          )}
        </div>

        <div className={s.textCol}>
          {headline && (
            <FadeIn delay={0.1}>
              <AnimateText element="h2" className={s.headline}>
                {headline}
              </AnimateText>
            </FadeIn>
          )}
          {body && (
            <HtmlRenderer
              animate
              html={body}
              element="div"
              className={s.body}
              delay={0.2}
            />
          )}
          {cta && (
            <FadeIn delay={0.2}>
              <Button
                cta={{ ...cta, variant: "tertiary" }}
                theme={configProps["data-typography"] || "light"}
                className={s.cta}
              />
            </FadeIn>
          )}
        </div>
      </div>
    </section>
  )
}
